import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Loader2, ShieldCheck, TrendingUp, AlertTriangle, MousePointerClick, Search, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Combined admin page: analytics + search tester.
 * Endpoints:
 *  - GET /api/admin/faqs/search-analytics
 *  - GET /api/admin/faqs/search-test?q=...
 */
export default function AdminSearchAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [testQ, setTestQ] = useState("");
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        setLoading(true);
        API.get("/admin/faqs/search-analytics").then(({ data }) => setAnalytics(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    }, []);

    const runTest = async () => {
        const q = testQ.trim();
        if (q.length < 2) { toast.error("Almeno 2 caratteri"); return; }
        setTesting(true);
        try {
            const { data } = await API.get("/admin/faqs/search-test", { params: { q } });
            setTestResult(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setTesting(false); }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <h1 className="mt-1 font-serif text-3xl font-semibold inline-flex items-center gap-2" data-testid="admin-search-analytics-title">
                <TrendingUp className="size-6 text-primary" /> Analitica ricerca
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">Cosa cercano gli utenti, cosa NON trovano, cosa cliccano. Nessun dato personale, aggregati anonimi.</p>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {/* ---- Search tester ---- */}
            <Card className="mt-6 border-primary/30" data-testid="admin-search-tester">
                <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <Play className="size-5 text-primary" />
                        <div className="font-serif text-xl font-semibold">Testa la ricerca</div>
                    </div>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input value={testQ} onChange={e => setTestQ(e.target.value)}
                                placeholder='Es. "il mio amministratore non risponde"'
                                onKeyDown={e => e.key === "Enter" && runTest()}
                                className="pl-9" data-testid="search-tester-input" />
                        </div>
                        <Button onClick={runTest} disabled={testing} data-testid="search-tester-btn">
                            {testing ? <Loader2 className="size-4 animate-spin" /> : "Analizza"}
                        </Button>
                    </div>

                    {testResult && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3 text-sm" data-testid="search-tester-result">
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                <div><span className="text-muted-foreground">Tokens:</span> <code>{(testResult.tokens || []).join(", ") || "—"}</code></div>
                                {Object.keys(testResult.fuzzy_map || {}).length > 0 && (
                                    <div><span className="text-muted-foreground">Correzioni fuzzy:</span> <code>{Object.entries(testResult.fuzzy_map).map(([k, v]) => `${k}→${v}`).join(", ")}</code></div>
                                )}
                                <div><span className="text-muted-foreground">Espansi (sinonimi):</span> <code>{(testResult.expanded || []).join(", ")}</code></div>
                                <div><span className="text-muted-foreground">Risultati:</span> <strong>{testResult.count}</strong></div>
                            </div>
                            {(testResult.results || []).length === 0 ? (
                                <p className="text-muted-foreground italic">Nessun risultato sopra la soglia di pertinenza (3.0).</p>
                            ) : (
                                <ol className="space-y-2">
                                    {testResult.results.map((r, i) => (
                                        <li key={i} className="rounded-lg bg-white border border-slate-200 p-3">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-900">#{i + 1} · {r.faq.title}</span>
                                                <span className="text-xs font-mono bg-primary text-primary-foreground rounded-full px-2 py-0.5">score {r.score}</span>
                                            </div>
                                            <div className="mt-2 text-xs text-muted-foreground font-mono break-all">
                                                {Object.entries(r.breakdown || {}).map(([k, v]) => (
                                                    <span key={k} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 mb-1">
                                                        {k}: {typeof v === "object" ? `+${v.contrib} (${v.unique}u/${v.occ}o × w${v.weight})` : String(v)}
                                                    </span>
                                                ))}
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ---- Analytics ---- */}
            {loading ? <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                : analytics && (
                    <div className="mt-6 grid gap-6 lg:grid-cols-2">
                        <Card data-testid="analytics-top">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <TrendingUp className="size-5 text-primary" />
                                    <h2 className="font-serif text-xl font-semibold">Ricerche più frequenti</h2>
                                </div>
                                {analytics.top_queries.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna ricerca registrata.</p> :
                                    <ol className="space-y-1.5 text-sm">
                                        {analytics.top_queries.map((q, i) => (
                                            <li key={q._id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
                                                <span className="truncate"><span className="text-muted-foreground mr-2">#{i + 1}</span>{q._id}</span>
                                                <span className="text-xs font-semibold text-primary">{q.count}×</span>
                                            </li>
                                        ))}
                                    </ol>
                                }
                            </CardContent>
                        </Card>

                        <Card data-testid="analytics-zero">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="size-5 text-amber-600" />
                                    <h2 className="font-serif text-xl font-semibold">Ricerche senza risultato</h2>
                                </div>
                                {analytics.zero_result_queries.length === 0 ? <p className="text-sm text-muted-foreground">Ottimo, nessuna ricerca a vuoto.</p> :
                                    <ol className="space-y-1.5 text-sm">
                                        {analytics.zero_result_queries.map((q, i) => (
                                            <li key={q._id} className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-1.5">
                                                <span className="truncate"><span className="text-muted-foreground mr-2">#{i + 1}</span>{q._id}</span>
                                                <span className="text-xs font-semibold text-amber-700">{q.count}×</span>
                                            </li>
                                        ))}
                                    </ol>
                                }
                            </CardContent>
                        </Card>

                        <Card className="lg:col-span-2" data-testid="analytics-clicks">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <MousePointerClick className="size-5 text-primary" />
                                    <h2 className="font-serif text-xl font-semibold">FAQ più cliccate</h2>
                                </div>
                                {analytics.most_clicked.length === 0 ? <p className="text-sm text-muted-foreground">Ancora nessun click registrato.</p> :
                                    <ol className="space-y-1.5 text-sm">
                                        {analytics.most_clicked.map((c, i) => (
                                            <li key={c.faq_id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
                                                <span className="truncate"><span className="text-muted-foreground mr-2">#{i + 1}</span>{c.title}</span>
                                                <span className="text-xs font-semibold text-primary">{c.clicks} click</span>
                                            </li>
                                        ))}
                                    </ol>
                                }
                            </CardContent>
                        </Card>
                    </div>
                )
            }
        </div>
    );
}
